import React, { useEffect, useRef, useState } from 'react';
import './NewProcessPopup.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import Textfield from '../input/Textfield';
import LocationSelect from '../location/LocationSelect';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { colorVar } from '../../util/participantGroups';
import { invalidateAddOns } from '../../hooks/useAddOns';
import AssignableEventList from './AssignableEventList';
import ProcessNotice from './ProcessNotice';
import { apiErrorMessage, ruleBadges, stepIcon } from './processUtils';

export const NEW_PROCESS_POPUP = 'processnew';

// Cache key of one unassigned-events request (template rules × process location).
const unassignedKey = (templateId, locationId) => `${templateId}:${locationId}`;

/**
 * "Neuer Prozess": choose a process template (cards), the location of the process (required — every
 * event of the process takes it), optional name and existing events of that location
 * (GET /EventProcess/GetUnassigned?templateId=&locationId=, loaded once per template × location while the
 * popup is open) → POST /EventProcess/Create. templates: non-archived ProcessTemplateResponse[] in display
 * order; locationsApi: the page's shared useLocations() instance (fetched on the first open).
 */
function NewProcessPopup({ templates, templatesLoaded, locationsApi, onCreated }) {
  const { isPopupOpen, popupData, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const isOpen = isPopupOpen && popupData.name === NEW_PROCESS_POPUP;
  const {
    locations, loading: locationsLoading, ensureLoaded: ensureLocationsLoaded, upsert: upsertLocation,
  } = locationsApi;

  const [templateId, setTemplateId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [name, setName] = useState('');
  const [assignIds, setAssignIds] = useState([]);
  const [unassigned, setUnassigned] = useState({}); // { [templateId:locationId]: AssignableEventDto[] }
  const [loadingKey, setLoadingKey] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Template × location combinations requested in this popup session (no repeated requests on re-select).
  const requestedRef = useRef(new Set());
  const templatesRef = useRef(templates);
  templatesRef.current = templates;

  // Fresh form on every open; a single template is preselected.
  useEffect(() => {
    if (!isOpen) return;
    const list = templatesRef.current ?? [];
    setTemplateId(list.length === 1 ? list[0].id : null);
    setLocationId(null);
    setName('');
    setAssignIds([]);
    setUnassigned({});
    setError('');
    requestedRef.current = new Set();
    ensureLocationsLoaded();
  }, [isOpen, ensureLocationsLoaded]);

  // Exactly one location → preselect (also when the list arrives after opening).
  useEffect(() => {
    if (isOpen && locationId == null && locations.length === 1) setLocationId(locations[0].id);
  }, [isOpen, locations, locationId]);

  // Events only once template and location are chosen — the list shows events of that location only.
  // The commit that opens the popup still carries the previous session's selection (the reset above
  // applies with the next render; the location always passes through null) — never load for it.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened || !isOpen || templateId == null || locationId == null) return;
    const key = unassignedKey(templateId, locationId);
    if (requestedRef.current.has(key)) return;
    requestedRef.current.add(key);
    setLoadingKey(key);
    api.get(`/EventProcess/GetUnassigned?templateId=${templateId}&locationId=${locationId}`)
      .then(r => setUnassigned(prev => ({ ...prev, [key]: Array.isArray(r.data) ? r.data : [] })))
      .catch(() => {
        // Retry on the next selection.
        requestedRef.current.delete(key);
        setUnassigned(prev => ({ ...prev, [key]: [] }));
      })
      .finally(() => setLoadingKey(k => (k === key ? null : k)));
  }, [isOpen, templateId, locationId]);

  const selectTemplate = (id) => {
    if (id === templateId) return;
    setTemplateId(id);
    // Events are template-specific (compatible rule set).
    setAssignIds([]);
    setError('');
  };

  const selectLocation = (id) => {
    if (id === locationId) return;
    setLocationId(id);
    // Events are location-specific (one location per process).
    setAssignIds([]);
    setError('');
  };

  const toggleAssign = (id) => {
    setAssignIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const handleCreate = () => {
    if (saving) return;
    if (templateId == null) {
      setError('Bitte eine Vorlage wählen.');
      return;
    }
    if (locationId == null) {
      setError('Bitte einen Ort wählen.');
      return;
    }
    setSaving(true);
    setError('');
    startSuccessBarLoading('Prozess wird angelegt');
    api.post('/EventProcess/Create', {
      name: name.trim() || null,
      eventIds: assignIds,
      processTemplateId: templateId,
      locationId,
    })
      .then(response => {
        // The new process copied add-on rows — the catalog's usage counts are refetched on its next load.
        invalidateAddOns();
        startSuccessBarSuccess('Prozess angelegt');
        closePopup();
        onCreated(response.data);
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorMessage(err, 'Prozess konnte nicht angelegt werden.'));
      })
      .finally(() => setSaving(false));
  };

  const selected = (templates ?? []).find(t => t.id === templateId) ?? null;
  const key = templateId != null && locationId != null ? unassignedKey(templateId, locationId) : null;
  const events = key != null ? unassigned[key] : null;

  return (
    <Popup
      name={NEW_PROCESS_POPUP}
      headerText={'Neuer Prozess'}
      confirmText={'Anlegen'}
      type={'info'}
      onConfirm={handleCreate}
      dontCloseOnConfirm
      withCancel
      wide
    >
      <div className={'newProcessForm'}>
        {error && <ProcessNotice tone={'error'}>{error}</ProcessNotice>}

        <h3>Vorlage</h3>
        {!templatesLoaded ? (
          <p className={'formFieldHint'}>Vorlagen werden geladen …</p>
        ) : (templates ?? []).length === 0 ? (
          <p className={'formFieldHint'}>
            Keine Prozessvorlagen vorhanden. Lege unter „Prozessvorlagen" (Startseite) eine Vorlage an.
          </p>
        ) : (
          <div className={'processTemplateCards'} role={'radiogroup'} aria-label={'Vorlage des Prozesses'}>
            {templates.map(t => {
              const isSelected = t.id === templateId;
              const badges = ruleBadges(t);
              return (
                <button
                  key={t.id}
                  type={'button'}
                  role={'radio'}
                  aria-checked={isSelected}
                  className={`processTemplateCard${isSelected ? ' isSelected' : ''}`}
                  style={{ '--process-template-color': colorVar(t.colorKey) }}
                  onClick={() => selectTemplate(t.id)}
                >
                  <span className={'processTemplateCardIcon material-symbols-rounded'} aria-hidden={'true'}>
                    {t.icon || 'checklist'}
                  </span>
                  <span className={'processTemplateCardBody'}>
                    <span className={'processTemplateCardName'}>{t.name}</span>
                    {t.description && <span className={'processTemplateCardDesc'}>{t.description}</span>}
                    {(t.steps ?? []).length > 0 && (
                      <span className={'processTemplateCardSteps'} aria-label={'Schritte'}>
                        {t.steps.map((s, idx) => (
                          <span key={s.id ?? idx} className={'processTemplateCardStep'}>
                            <span className={'material-symbols-rounded processTemplateCardStepIcon'} aria-hidden={'true'}>
                              {stepIcon(s)}
                            </span>
                            {s.label}
                          </span>
                        ))}
                      </span>
                    )}
                    {badges.length > 0 && (
                      <span className={'processTemplateCardRules'}>
                        {badges.map(b => b.label).join(' · ')}
                      </span>
                    )}
                  </span>
                  <span className={'processTemplateCardCheck material-symbols-rounded'} aria-hidden={'true'}>
                    {isSelected ? 'radio_button_checked' : 'radio_button_unchecked'}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {selected && (
          <React.Fragment>
            <h3>Ort</h3>
            <LocationSelect
              label={null}
              locations={locations}
              loading={locationsLoading}
              value={locationId}
              onChange={selectLocation}
              onLocationSaved={upsertLocation}
            />
            <p className={'formFieldHint'}>
              Gilt für alle Events des Prozesses. Mails nutzen automatisch die Textbaustein-Variante dieses Orts,
              falls es eine gibt.
            </p>

            <h3>Name (optional)</h3>
            <Textfield
              icon={'label'}
              type={'text'}
              placeholder={'Automatisch aus den Event-Daten'}
              value={name}
              maxLength={100}
              onChange={e => setName(e.target.value)}
            />

            <h3>Bestehende Events übernehmen (optional)</h3>
            {locationId == null ? (
              <p className={'formFieldHint'}>Wähle zuerst einen Ort – dann erscheinen hier die passenden Events.</p>
            ) : (
              <React.Fragment>
                <AssignableEventList
                  events={events}
                  selectedIds={assignIds}
                  onToggle={toggleAssign}
                  loading={loadingKey === key && events == null}
                  emptyText={'Keine passenden Events ohne Prozess an diesem Ort vorhanden.'}
                />
                <p className={'formFieldHint'}>
                  Nur künftige Events an diesem Ort mit passenden Regeln, die noch keinem Prozess zugeordnet sind.
                  Weitere Events kannst du später über „+ Event" hinzufügen oder neu anlegen.
                </p>
                {(events ?? []).length > 0 && (
                  <p className={'formFieldHint'}>
                    Übernommene Events erhalten die Zusätze des neuen Prozesses: Buchungen von Zusätzen, die der
                    Prozess nicht anbietet, werden entfernt; offene Preise werden neu berechnet (bezahlte und manuell
                    gesetzte bleiben).
                  </p>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
    </Popup>
  );
}

export default NewProcessPopup;
